import { useAppSelector } from '@hooks/reduxHooks'
import { AccessEnum, RoleDto } from '@services/api'
import { useCallback, useMemo } from 'react'

export function usePermissionsRole(siteId?: number) {
  const { user } = useAppSelector(({ auth }) => ({
    user: auth.user,
  }))

  const userRoles = useMemo(
    () => (user as unknown as { roles: RoleDto[] })?.roles || [],
    [user],
  )

  const allPermissions = useMemo(() => {
    const set = new Set<AccessEnum>()
    userRoles.forEach((role) => {
      role.permissions.forEach((p) => {
        if (p.permission) set.add(p.permission)
      })
    })
    return set
  }, [userRoles])

  const permissionsBySiteMap = useMemo(() => {
    const map = new Map<number, Set<AccessEnum>>()
    userRoles.forEach((role) => {
      if (!role.site?.id) return
      if (!map.has(role.site.id)) {
        map.set(role.site.id, new Set<AccessEnum>())
      }
      const sitePermissions = map.get(role.site.id)!
      role.permissions.forEach((p) => {
        if (p.permission) sitePermissions.add(p.permission)
      })
    })
    return map
  }, [userRoles])

  const currentSitePermissions = siteId
    ? permissionsBySiteMap.get(siteId)
    : null

  const hasPermission = useCallback(
    (permission: AccessEnum): boolean => {
      if (siteId) {
        return currentSitePermissions?.has(permission) ?? false
      }
      return allPermissions.has(permission)
    },
    [siteId, currentSitePermissions, allPermissions],
  )

  return { hasPermission }
}
